import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import AddWorkoutDetails from "../AddWorkoutDetails";

const WorkoutImport = () => {
  const { getClients, addWorkoutForUser, importWorkouts } = useApi();
  const { isCoach, loading: userInfoLoading, id: coachId, name: coachName, email: coachEmail } = useUserInfo();
  const [clients, setClients] = useState([]);
  const [coachProfile, setCoachProfile] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [rawText, setRawText] = useState("");
  const [defaultWeightUnit, setDefaultWeightUnit] = useState("lbs");
  const [weeks, setWeeks] = useState([]);
  const [programLength, setProgramLength] = useState(1);
  const [selectedWorkoutKey, setSelectedWorkoutKey] = useState(null); // Format: "week-{weekNum}-{workoutIdx}"
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [savedDates, setSavedDates] = useState({});

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const res = await getClients();
        const active = res.filter((c) => !c.deleted);
        setClients(active);
        
        // Create coach profile object
        if (coachId && coachName) {
          const coach = {
            id: coachId,
            name: coachName,
            email: coachEmail || "",
            isCoach: true,
          };
          setCoachProfile(coach);
          // Prepend coach to clients list and set as default selection
          const allClients = [coach, ...active];
          setSelectedClientId(coachId);
        } else if (active.length) {
          setSelectedClientId(active[0].id);
        }
      } catch (err) {
        console.error(err);
        setError("Unable to load clients.");
      } finally {
        setLoadingClients(false);
      }
    };
    loadClients();
  }, [getClients, coachId, coachName, coachEmail]);

  const handleFileUpload = (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setRawText(e.target.result);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setError("");
    setStatusMessage("");
    setWeeks([]);
    setSelectedWorkoutKey(null);
    if (!rawText.trim()) {
      setError("Paste or upload workout text before importing.");
      return;
    }
    try {
      setImporting(true);
      const resp = await importWorkouts({
        rawText,
        defaultWeightUnit,
      });
      const weeksData = resp?.weeks || [];
      const programLen = resp?.programLength || 1;

      if (!weeksData.length) {
        setError("No workouts found in the provided text.");
        return;
      }

      // Process weeks data
      const processedWeeks = weeksData.map((weekData) => ({
        week: weekData.week,
        workouts: (weekData.workouts || []).map((w) => ({
          clientTempId: w.clientTempId,
          order: w.order,
          warnings: w.warnings || [],
          workout: {
            ...(w.workout || {}),
            exercises: w.workout?.exercises || [],
          },
        })),
      }));

      setWeeks(processedWeeks);
      setProgramLength(programLen);

      // Select first workout of first week
      if (processedWeeks.length > 0 && processedWeeks[0].workouts.length > 0) {
        setSelectedWorkoutKey(`week-${processedWeeks[0].week}-0`);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to import workouts. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const handleSaveSingle = async (payload) => {
    if (!selectedClientId) {
      alert("Select a client before saving.");
      return;
    }
    if (!payload?.date) {
      alert("Please pick a date before saving.");
      return;
    }
    if (!selectedWorkoutKey) {
      alert("No workout selected.");
      return;
    }

    setSaving(true);
    try {
      await addWorkoutForUser(selectedClientId, payload);
      const savedDate = payload.date;

      // Parse selected workout key
      const [, weekNum, workoutIdx] = selectedWorkoutKey.split("-");
      const weekNumber = parseInt(weekNum, 10);
      const workoutIndex = parseInt(workoutIdx, 10);

      // Track that this workout was saved
      setSavedDates((prev) => ({
        ...prev,
        [selectedWorkoutKey]: savedDate,
      }));
      
      // Update workout state with saved data (date as unix timestamp)
      // The date conversion in AddWorkoutDetails should handle this correctly
      const dateUnix = typeof payload.date === 'number' 
        ? payload.date 
        : moment.isMoment(payload.date) 
        ? payload.date.unix() 
        : moment(payload.date).unix();
      
      setWeeks((prev) =>
        prev.map((week) =>
          week.week === weekNumber
            ? {
                ...week,
                workouts: week.workouts.map((item, idx) =>
                  idx === workoutIndex
                    ? {
                        ...item,
                        workout: {
                          ...payload,
                          date: dateUnix,
                        },
                      }
                    : item
                ),
              }
            : week
        )
      );
      setStatusMessage(
        `Workout saved to ${moment(savedDate).format("MMM D, YYYY")}`
      );
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to save workout. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (userInfoLoading) {
    return (
      <Container sx={{ mt: 8 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!isCoach) {
    return (
      <Container sx={{ mt: 8 }}>
        <Alert severity="warning">
          This page is only available to coaches.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 6, mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Import Workouts
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Paste or upload workout text and we&apos;ll format it. Pick a client,
        preview each imported workout, set a date, and save.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {loadingClients ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  height: 56,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <CircularProgress size={24} />
                <Typography color="text.secondary">Loading clients...</Typography>
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="client-select-label">Client</InputLabel>
                <Select
                  labelId="client-select-label"
                  label="Client"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  {coachProfile && (
                    <MenuItem key={coachProfile.id} value={coachProfile.id}>
                      {coachProfile.name} (Me) — {coachProfile.email}
                    </MenuItem>
                  )}
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name} — {client.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              label="Default Weight Unit"
              fullWidth
              size="small"
              value={defaultWeightUnit}
              onChange={(e) => setDefaultWeightUnit(e.target.value)}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="kg">Kilograms (kg)</MenuItem>
              <MenuItem value="bodyweight">Bodyweight</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ mt: { xs: 2, md: 0 } }}
            >
              Upload .txt
              <input
                type="file"
                hidden
                accept=".txt,.md,text/plain"
                onChange={handleFileUpload}
              />
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing}
              fullWidth
            >
              {importing ? "Importing..." : "Import"}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Paste workout text"
              placeholder="Paste the workout text here..."
              multiline
              minRows={10}
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {statusMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h6">Imported Workouts</Typography>
          {weeks.length > 0 && programLength > 1 && (
            <Chip
              label={`${programLength} Week Program`}
              color="primary"
              size="small"
            />
          )}
        </Box>
        {!weeks.length ? (
          <Typography color="text.secondary">
            No workouts parsed yet. Paste or upload text, then click Import.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                {weeks.map((weekData) => (
                  <Accordion
                    key={weekData.week}
                    defaultExpanded={weekData.week === 1}
                    sx={{
                      "&:before": { display: "none" },
                      boxShadow: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Week {weekData.week}
                        {weekData.workouts.length > 0 && (
                          <Chip
                            label={`${weekData.workouts.length} workout${
                              weekData.workouts.length !== 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              ml: 1,
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              color: "inherit",
                            }}
                          />
                        )}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {weekData.workouts.map((item, idx) => {
                          const workoutKey = `week-${weekData.week}-${idx}`;
                          const isSelected = selectedWorkoutKey === workoutKey;
                          return (
                            <Paper
                              key={workoutKey}
                              variant="outlined"
                              sx={{
                                p: 2,
                                border: isSelected
                                  ? "2px solid #ff0000"
                                  : "1px solid #e0e0e0",
                                cursor: "pointer",
                                backgroundColor: isSelected
                                  ? "action.selected"
                                  : "background.paper",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                              onClick={() => setSelectedWorkoutKey(workoutKey)}
                            >
                              <Typography variant="subtitle1" noWrap>
                                {item.workout?.name || "Untitled Workout"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {savedDates[workoutKey]
                                  ? `Saved ${moment(
                                      savedDates[workoutKey]
                                    ).format("MMM D")}`
                                  : moment().format("dddd, MMM D")}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {item.workout?.exercises?.length || 0} exercises
                              </Typography>
                              {item.warnings && item.warnings.length > 0 && (
                                <Chip
                                  label={`${item.warnings.length} warning${
                                    item.warnings.length !== 1 ? "s" : ""
                                  }`}
                                  size="small"
                                  color="warning"
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </Paper>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedWorkoutKey ? (
                (() => {
                  const [, weekNum, workoutIdx] = selectedWorkoutKey.split("-");
                  const weekNumber = parseInt(weekNum, 10);
                  const workoutIndex = parseInt(workoutIdx, 10);
                  const selectedWeek = weeks.find((w) => w.week === weekNumber);
                  const selectedWorkout = selectedWeek?.workouts[workoutIndex];

                  if (!selectedWorkout) {
                    return (
                      <Typography color="text.secondary">
                        Workout not found.
                      </Typography>
                    );
                  }

                  // Helper to convert date to moment - simplified
                  const getMomentDate = (date) => {
                    if (!date) return moment();
                    if (moment.isMoment(date)) return date.clone();
                    if (typeof date === 'number') return moment.unix(date);
                    return moment(date);
                  };
                  
                  // Convert date to moment object for the form
                  const workoutWithDate = {
                    ...(selectedWorkout.workout || {}),
                    date: getMomentDate(selectedWorkout.workout?.date),
                  };

                  return (
                    <AddWorkoutDetails
                      key={selectedWorkoutKey}
                      mode="workout"
                      userId={selectedClientId}
                      workout={workoutWithDate}
                      onSave={handleSaveSingle}
                      handleClose={() => {}}
                      draftTemplateMode
                      submitButtonId="import-workout-save-button"
                      onDeleteDraft={() => {
                        setWeeks((prev) =>
                          prev
                            .map((week) =>
                              week.week === weekNumber
                                ? {
                                    ...week,
                                    workouts: week.workouts.filter(
                                      (_, i) => i !== workoutIndex
                                    ),
                                  }
                                : week
                            )
                            .filter((week) => week.workouts.length > 0)
                        );
                        // Select next available workout
                        const updatedWeeks = weeks
                          .map((week) =>
                            week.week === weekNumber
                              ? {
                                  ...week,
                                  workouts: week.workouts.filter(
                                    (_, i) => i !== workoutIndex
                                  ),
                                }
                              : week
                          )
                          .filter((week) => week.workouts.length > 0);

                        if (updatedWeeks.length > 0) {
                          const firstWeek = updatedWeeks[0];
                          if (firstWeek.workouts.length > 0) {
                            setSelectedWorkoutKey(`week-${firstWeek.week}-0`);
                          } else {
                            setSelectedWorkoutKey(null);
                          }
                        } else {
                          setSelectedWorkoutKey(null);
                        }
                      }}
                    />
                  );
                })()
              ) : (
                <Typography color="text.secondary">
                  Select a workout to edit and save.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default WorkoutImport;
